import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, Printer } from "lucide-react";
import { format } from "date-fns";

export default function AccessDeskTicket() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from("day_passes" as any)
        .select(`
          id,
          ticket_code,
          amount,
          currency,
          pass_date,
          party_size,
          payment_method,
          payment_status,
          created_at,
          customer:customers(name,phone,email),
          business:businesses(
            id,
            name,
            phone,
            logo_url,
            primary_color,
            location_id
          )
        `)
        .eq("id", id)
        .single();
      
      // Fetch business location address
      let businessAddress = null;
      if (data?.business?.id) {
        const business = data.business as any;
        if (business.location_id) {
          const { data: location } = await supabase
            .from("locations")
            .select("address")
            .eq("id", business.location_id)
            .maybeSingle();
          businessAddress = location?.address || null;
        } else {
          // Fallback to default location
          const { data: location } = await supabase
            .from("locations")
            .select("address")
            .eq("business_id", business.id)
            .eq("is_default", true)
            .maybeSingle();
          businessAddress = location?.address || null;
        }
      }
      
      // Fetch payment info if paid via Paystack
      let paymentInfo = null;
      if (data?.payment_method === "paystack" && data?.id) {
        const { data: payment } = await supabase
          .from("platform_payments")
          .select("provider_reference, paid_at, status")
          .eq("context", "day_pass_payment")
          .eq("context_id", data.id)
          .eq("status", "success")
          .order("paid_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        paymentInfo = payment;
      }
      if (error) setError(error.message);
      setData({ ...data, paymentInfo, businessAddress });
      setLoading(false);
    };
    run();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background p-6">
        <p className="text-sm text-destructive">Failed to load ticket: {error || "Not found"}</p>
      </div>
    );
  }

  const printedAt = format(new Date(), "MMM d, yyyy h:mm a");
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.ticket_code)}`;
  const businessAddress = data.businessAddress || null;
  const paymentMethodLabel = data.payment_method === "paystack" ? "Paystack (Card/Mobile Money)" : 
                            data.payment_method === "cash" ? "Cash" : 
                            data.payment_method === "mtn_momo" ? "MTN Mobile Money" :
                            data.payment_method === "vodafone_cash" ? "Vodafone Cash" :
                            data.payment_method || "—";

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-md">
        <div className="flex items-center justify-between gap-3 mb-4 print:hidden">
          <Button variant="outline" onClick={() => window.close()}>
            Close
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>

        <div className="rounded-xl border-2 border-border bg-card p-6 print:border-2 print:shadow-none print:rounded-none print:p-4">
          {/* Business Header */}
          <div className="text-center mb-4">
            <p className="text-xl font-bold text-foreground">{data.business?.name || "Buukd"}</p>
            {businessAddress ? (
              <p className="text-xs text-muted-foreground mt-1">{businessAddress}</p>
            ) : null}
            {data.business?.phone ? (
              <p className="text-xs text-muted-foreground mt-1">{data.business.phone}</p>
            ) : null}
          </div>

          <div className="border-t-2 border-border pt-4 mb-4">
            <p className="text-xs text-muted-foreground text-center uppercase tracking-wider font-semibold">Day Pass Ticket</p>
            <p className="mt-2 text-center font-mono text-3xl tracking-widest font-bold">{data.ticket_code}</p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center my-4">
            <img 
              src={qrCodeUrl} 
              alt="Ticket QR Code" 
              className="h-40 w-40 border-2 border-border rounded-lg p-2 bg-white print:h-36 print:w-36"
              onError={(e) => {
                console.error("QR code failed to load:", qrCodeUrl);
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>

          {/* Customer & Ticket Details */}
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between py-1">
              <span className="text-muted-foreground">Name</span>
              <span className="font-semibold text-foreground text-right">{data.customer?.name || "—"}</span>
            </div>
            {data.customer?.phone && (
              <div className="flex items-center justify-between py-1">
                <span className="text-muted-foreground">Phone</span>
                <span className="font-medium text-foreground text-right">{data.customer.phone}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-1">
              <span className="text-muted-foreground">Date</span>
              <span className="font-medium text-foreground">{data.pass_date}</span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-muted-foreground">Party Size</span>
              <span className="font-medium text-foreground">{Number(data.party_size || 1)} {Number(data.party_size || 1) > 1 ? "people" : "person"}</span>
            </div>
          </div>

          {/* Payment Section */}
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Payment Details</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-bold text-foreground text-lg">
                  {data.currency} {Number(data.amount || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Payment Method</span>
                <span className="font-medium text-foreground">{paymentMethodLabel}</span>
              </div>
              {data.paymentInfo?.provider_reference && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono text-xs text-foreground">{data.paymentInfo.provider_reference}</span>
                </div>
              )}
              {data.paymentInfo?.paid_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Paid At</span>
                  <span className="font-medium text-foreground text-xs">
                    {format(new Date(data.paymentInfo.paid_at), "MMM d, yyyy h:mm a")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Terms & Footer */}
          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-left leading-relaxed">
              This ticket is valid for the date shown above. Please present this ticket or QR code upon entry.
            </p>
            <p className="text-xs text-muted-foreground text-left mt-2">
              Printed {printedAt}
            </p>
            <p className="text-xs text-muted-foreground text-left mt-1">
              Receipt #: {data.ticket_code}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


